import {
  focusManager,
  type InvalidateQueryFilters,
  QueryClient,
  type QueryKey,
  useQueryClient,
} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {Throttler} from '@tanstack/react-pacer'
import {queryKeyHashFn} from '@github-ui/query-client/query-key-hash-fn'

/**
 * Minimal shape every alive payload is expected to have. `wait` accounts for replication lag: the publisher asks
 * consumers to hold off for this many milliseconds before reading.
 */
export interface AliveUpdateData {
  wait?: number
}

export interface UseInvalidateOnAliveUpdateConfig<TData extends AliveUpdateData = AliveUpdateData> {
  /** Option filter for events; return `false` to ignore an event. */
  filter?: (data: TData) => boolean
  /** Throttle timeout to limit frequency of invalidations. Defaults to 2s. */
  throttleTimeout?: number
}

const isQueryKey = (queryKeyOrFilters: QueryKey | InvalidateQueryFilters): queryKeyOrFilters is QueryKey =>
  Array.isArray(queryKeyOrFilters)

const buildInvalidateFilters = (queryKeyOrFilters: QueryKey | InvalidateQueryFilters) =>
  isQueryKey(queryKeyOrFilters) ? {queryKey: queryKeyOrFilters, exact: true} : queryKeyOrFilters

interface RegistryEntry {
  /** Map of currently mounted hook IDs to `wait` ms config. */
  mountedHooks: Map<string, number>
  throttler: Throttler<() => Promise<void>>
}

/** Global registry of throttled `invalidate` functions per key. */
const GlobalThrottledInvalidatorsStore = new (class {
  /**
   * Query client -> query key/filter -> registry entry. Mapping by query client is probably unecessary because we
   * only ever use one client in our app, but it's more robust in case a second client is ever introduced.
   */
  #registry = new Map<QueryClient, Map<string, RegistryEntry>>()

  register(
    client: QueryClient,
    filters: InvalidateQueryFilters,
    queryKeyHash: string,
    hookId: string,
    wait: number,
  ): () => void {
    const clientRegistry = this.#registry.get(client) ?? new Map<string, RegistryEntry>()
    this.#registry.set(client, clientRegistry)

    const existingEntry = clientRegistry.get(queryKeyHash)

    if (existingEntry) {
      // Optimization: if the entry already exists and matches, there's no need to do anything
      if (existingEntry.mountedHooks.get(hookId) === wait) return existingEntry.throttler.maybeExecute

      existingEntry.mountedHooks.set(hookId, wait)

      // Recalculate max wait time with new entry
      const maxWait = Math.max(...existingEntry.mountedHooks.values())
      existingEntry.throttler.setOptions({wait: maxWait})

      return existingEntry.throttler.maybeExecute
    } else {
      const throttler = new Throttler(() => client.invalidateQueries(filters), {
        wait,
        // `trailing: false` may discard subsequent rapid updates, which is technically less correct as it could skip
        // an update. However, if we have `trailing` and `leading` both enabled, two simultaneous updates from different
        // subscribers will result in two events, even though the whole point of this hook is to deduplicate.
        // From a correctness standpoint, `trailing: true, leading: false` would be better, but that would delay every
        // update by the throttle delay + the event's `wait` duration.
        trailing: false,
        leading: true,
      })

      clientRegistry.set(queryKeyHash, {
        mountedHooks: new Map([[hookId, wait]]),
        throttler,
      })

      return throttler.maybeExecute
    }
  }

  deregister(client: QueryClient, queryKeyHash: string, hookId: string) {
    const clientRegistry = this.#registry.get(client)
    if (!clientRegistry) return

    const existingEntry = clientRegistry.get(queryKeyHash)
    if (!existingEntry) return

    existingEntry.mountedHooks.delete(hookId)

    if (existingEntry.mountedHooks.size === 0) {
      // this was the last relevant mounted hook, so we can delete the entry entirely
      clientRegistry.delete(queryKeyHash)
      if (clientRegistry.size === 0) this.#registry.delete(client)
      return
    }

    // Recalculate max wait time with remaining entries
    const maxWait = Math.max(...existingEntry.mountedHooks.values())
    existingEntry.throttler.setOptions({wait: maxWait})
  }
})()

/**
 * Invalidate a query whenever a matching alive update arrives on a channel. Throttled across multiple updates for the
 * same query key.
 *
 * Note that invalidation is globally throttled for all mounted hooks using the same `queryKeyOrFilters`. If hooks mount
 * with different `config.throttleTimeout` times, the largest throttle time will always be used.
 *
 * @param queryKeyOrFilters If a query key is passed (typical usage), will invalidate just this query with exact key matching.
 * For more custom key matching / multiple-query invalidation, a custom filters parameter can be provided.
 *
 * A limitation of this hook is that the throttling may not be hit if one mounted hook uses a strict query key and
 * another matches the same query with a broader filter. Use caution when using filters and prefer just passing a query
 * key array if possible.
 */
export function useInvalidateOnAliveUpdate<TData extends AliveUpdateData = AliveUpdateData>(
  channel: string | null | undefined,
  queryKeyOrFilters: QueryKey | InvalidateQueryFilters,
  config?: UseInvalidateOnAliveUpdateConfig<TData>,
) {
  const queryClient = useQueryClient()
  const hookId = useId()

  const filter = config?.filter
  const throttleTimeout = config?.throttleTimeout ?? 2000

  const throttledInvalidateRef = useRef<() => void>(undefined)
  const invalidateFilters = useMemo(() => buildInvalidateFilters(queryKeyOrFilters), [queryKeyOrFilters])
  const queryKeyHash = queryKeyHashFn([invalidateFilters])

  // This effect depends on queryKeyOrFilters so may run on every render, but that's OK because
  // GlobalThrottledInvalidatorsStore.register will just early-return if nothing actually changed.
  useEffect(() => {
    throttledInvalidateRef.current = GlobalThrottledInvalidatorsStore.register(
      queryClient,
      invalidateFilters,
      queryKeyHash,
      hookId,
      throttleTimeout,
    )
  }, [queryClient, invalidateFilters, queryKeyHash, hookId, throttleTimeout])

  // Clean up in a separate effect so cleanup doesn't depend on queryKeyOrFilters, which may change on every render.
  // This is important to avoid deleting and recreating the callback on every render, which would break throttling.
  useEffect(
    () => () => {
      GlobalThrottledInvalidatorsStore.deregister(queryClient, queryKeyHash, hookId)
      throttledInvalidateRef.current = undefined
    },
    [queryClient, queryKeyHash, hookId],
  )

  const [shouldInvalidateOnFocus, setShouldInvalidateOnFocus] = useState(false)

  // We can't rely on simply setting the query to stale in the background and waiting for window focus to trigger a
  // refetch, because we have `refetchOnWindowFocus` set to false globally on the query client.
  useEffect(() => {
    if (!shouldInvalidateOnFocus) return

    return focusManager.subscribe(focused => {
      if (!focused) return
      throttledInvalidateRef.current?.()
      setShouldInvalidateOnFocus(false)
    })
  }, [shouldInvalidateOnFocus])

  const handleNotification = useCallback(
    (data: TData) => {
      const shouldHandle = filter?.(data) ?? true
      if (!shouldHandle) return

      // Defer the update when the window is inactive
      const handleUpdate = () =>
        focusManager.isFocused() ? throttledInvalidateRef.current?.() : setShouldInvalidateOnFocus(true)

      if (data.wait !== undefined) setTimeout(handleUpdate, data.wait)
      else handleUpdate()
    },
    [filter],
  )

  useAlive(channel, handleNotification)
}
